import { useRef, useState } from "react";

import { Box, Button } from "@ezmid/components-library/";
import { faKey } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import * as yup from "yup";

import LoginFormWrapper from "./LoginFormWrapper";
import { useResetPasswordMutation } from "../../api/reset-password.generated";
import { Form } from "../../components/hookFormComponents/Form";
import { FormInputField } from "../../components/hookFormComponents/FormInputField";
import { handleValidationErrors } from "../../utils/validationErrorHandler";

type FormValues = {
  newPassword: string;
  confirmPassword: string;
};

export default function SetNewPasswordView() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [resetPassword] = useResetPasswordMutation();
  const [isSuccess, setIsSuccess] = useState(false);

  const FormSchema = yup.object().shape({
    newPassword: yup.string().required(t("form.password.required")),
    confirmPassword: yup
      .string()
      .required(t("form.password.confirm.required"))
      .oneOf([yup.ref("newPassword")], t("form.password.confirm.match")),
  });

  const {
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    control,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
    mode: "all",
    resolver: yupResolver(FormSchema),
  });

  const submitRef = useRef<HTMLButtonElement>(null);

  const onSubmit = () => {
    const { newPassword } = getValues();
    resetPassword({
      resetPasswordUpdateRequest: { newPassword, token },
    })
      .unwrap()
      .then(() => {
        setIsSuccess(true);
      })
      .catch((error) => {
        const { hasFieldErrors, errorMessage } = handleValidationErrors(
          error,
          setError
        );
        if (!hasFieldErrors) {
          toast.warning(errorMessage || t("app.password.reset.failed"));
        }
      });
  };

  return (
    <LoginFormWrapper>
      <h1 className="text-4xl mb-6">{t("app.auth.setNewPassword")}</h1>
      {isSuccess ? (
        <p className="text-green-600">{t("app.password.reset.success")}</p>
      ) : (
        <>
          <p className="text-gray-400 mb-4">
            {t("app.auth.enterNewPasswordDescription")}
          </p>
          <Form
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            submitRef={submitRef}
          >
            <>
              <FormInputField
                name="newPassword"
                htmlFor="newPassword"
                label={t("form.newPassword.label")}
                id="newPassword"
                type="password"
                control={control}
                error={errors.newPassword}
                placeholder={t("form.newPassword.label")}
              />
              <FormInputField
                name="confirmPassword"
                htmlFor="confirmPassword"
                label={t("form.confirmPassword.label")}
                id="confirmPassword"
                type="password"
                control={control}
                error={errors.confirmPassword}
                placeholder={t("form.confirmPassword.label")}
              />
            </>
          </Form>
          <Box display="flex">
            <Button
              type="button"
              variant="primary"
              leftIcon={<FontAwesomeIcon icon={faKey} />}
              onClick={() => onSubmit()}
              disabled={!isValid}
            >
              {t("app.auth.setNewPassword")}
            </Button>
          </Box>
        </>
      )}
    </LoginFormWrapper>
  );
}
